import React, { useState, useRef, useEffect } from "react";
import { Button } from "./button";
import { X, Upload, Check, AlertCircle } from "lucide-react";
import { UPLOAD_STATUS } from "../interfaces/UploadProgress";
import { toast } from "react-toastify";
import { validateUserRole } from "../../helpers/helpers";
import { userRoles } from "../../helpers/constant";

export const EnhancedUploadFileModal = ({
  open,
  onClose,
  onUploadComplete,
  collectionId,
  facilityId,
  // Facility that owns the collection being uploaded to. Used ONLY to tag the
  // created File / FileRelation with the right facility for superadmin
  // cross-facility uploads. Unlike `facilityId`, it does NOT switch the modal
  // into facility-context mode (md-only, facility_context relations).
  fileFacilityId = null,
  maxFiles,
  currentFileCount = 0,
  existingFileNames = [],
}) => {
  const isFacilityMode = !!facilityId;

  // Shared upload state
  const [uploadProgress, setUploadProgress] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [inputError, setInputError] = useState(null);

  // File tab state
  const [activeTab, setActiveTab] = useState("file");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Text tab state
  const [textContent, setTextContent] = useState("");
  const [textFileName, setTextFileName] = useState("");

  const shouldDisableInteractions = () => isUploading || uploadComplete;
  const remainingSlots = maxFiles != null ? maxFiles - currentFileCount : null;

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && !shouldDisableInteractions()) handleClose();
    };
    if (open) document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [open, isUploading, uploadComplete]);

  const resetState = () => {
    setUploadProgress([]);
    setIsUploading(false);
    setUploadComplete(false);
    setInputError(null);
    setSelectedFiles([]);
    setTextContent("");
    setTextFileName("");
    setActiveTab("file");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (isUploading) return;
    if (uploadComplete) {
      const successCount = uploadProgress.filter(
        (p) => p.status === UPLOAD_STATUS.SUCCESS
      ).length;
      if (successCount > 0) {
        toast.success(
          `${successCount} file${successCount !== 1 ? "s" : ""} uploaded successfully!`
        );
      }
    }
    resetState();
    onClose();
  };

  const handleBackgroundClick = (event) => {
    if (event.target === event.currentTarget && !shouldDisableInteractions()) {
      handleClose();
    }
  };

  if (!open) return null;

  // ── File tab: selection handler ──────────────────────────────────────────
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    if (isFacilityMode) {
      const nonMd = files.filter((f) => !f.name.toLowerCase().endsWith(".md"));
      if (nonMd.length > 0) {
        setInputError("Only .md (Markdown) files are allowed.");
        setSelectedFiles([]);
        setUploadProgress([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    if (maxFiles != null) {
      if (currentFileCount >= maxFiles) {
        setInputError(`File limit of ${maxFiles} reached. Delete existing files first.`);
        setSelectedFiles([]);
        setUploadProgress([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (currentFileCount + files.length > maxFiles) {
        setInputError(
          `Cannot upload ${files.length} file(s). Only ${remainingSlots} slot(s) remaining (limit: ${maxFiles}).`
        );
        setSelectedFiles([]);
        setUploadProgress([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    // Check for duplicates within the selected batch
    const selectedNames = files.map((f) => f.name.toLowerCase());
    const batchDuplicates = files
      .filter((f, i) => selectedNames.indexOf(f.name.toLowerCase()) !== i)
      .map((f) => f.name);
    if (batchDuplicates.length > 0) {
      setInputError(`Duplicate files in selection: ${[...new Set(batchDuplicates)].join(", ")}`);
      setSelectedFiles([]);
      setUploadProgress([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Check against already-uploaded files
    const existingNamesLower = existingFileNames.map((n) => n.toLowerCase());
    const alreadyUploaded = files.filter((f) =>
      existingNamesLower.includes(f.name.toLowerCase())
    );
    if (alreadyUploaded.length > 0) {
      setInputError(
        `File${alreadyUploaded.length > 1 ? "s" : ""} already uploaded: ${alreadyUploaded.map((f) => f.name).join(", ")}`
      );
      setSelectedFiles([]);
      setUploadProgress([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setInputError(null);
    setSelectedFiles(files);
    setUploadComplete(false);
    setUploadProgress(
      files.map((file) => ({
        file,
        name: file.name,
        progress: 0,
        status: UPLOAD_STATUS.UPLOADING,
        error: null,
      }))
    );
  };

  // ── Core upload pipeline (shared by both tabs) ───────────────────────────
  const performUpload = async (filesToUpload) => {
    setIsUploading(true);
    setUploadProgress(
      filesToUpload.map((f) => ({
        file: f,
        name: f.name,
        progress: 0,
        status: UPLOAD_STATUS.UPLOADING,
        error: null,
      }))
    );

    let successfulUploadsCount = 0;

    try {
      const { default: azureFunctionService } = await import(
        "../../services/azureFunctionService"
      );
      const { default: fileService } = await import("../../services/fileService");
      const { default: fileRelationService } = await import(
        "../../services/fileRelationService"
      );
      const { getCurrentUserId } = await import("../../helpers/auth");
      const {
        transformAzureResponseToFileData,
        createFileRelationPayload,
        createFacilityFileRelationPayload,
      } = await import("../../helpers/dataTransform");

      const userId = getCurrentUserId();
      if (!userId) throw new Error("User not authenticated");

      const isSuperAdmin = validateUserRole(userRoles.SUPERADMIN);

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        try {
          updateFileProgress(i, 0, UPLOAD_STATUS.UPLOADING);

          // Step 1: Upload to Azure Function (0% → 33%)
          // Facility uploads tag vectors with facility_context; collection
          // uploads tag them with the collection_id so the embedding Function
          // persists it onto the Qdrant payload (metadata.collection_id) and
          // retrieval can be scoped to a single collection's KB.
          const extraMetadata = facilityId
            ? {
                relation_type: "facility_context",
                ...(isSuperAdmin && { facility_id: facilityId }),
              }
            : collectionId
            ? { collection_id: collectionId }
            : {};
          const azureResponse = await azureFunctionService.uploadSingleFile(
            file,
            (progress) =>
              updateFileProgress(
                i,
                Math.round(progress * 0.33),
                UPLOAD_STATUS.UPLOADING
              ),
            extraMetadata
          );

          updateFileProgress(i, 33, UPLOAD_STATUS.UPLOADING);

          // Step 2: Create file record in backend (33% → 66%)
          // In facility mode the facility comes from `facilityId`; for a
          // collection upload it comes from the collection's own facility
          // (`fileFacilityId`). Either way, only superadmins may cross facilities.
          const effectiveFacilityId = facilityId ?? fileFacilityId;
          const fileData = transformAzureResponseToFileData(
            [azureResponse],
            userId,
            isSuperAdmin ? effectiveFacilityId : null
          );
          const fileCreationResponse = await fileService.createFiles(fileData);

          updateFileProgress(i, 66, UPLOAD_STATUS.UPLOADING);

          // Step 3: Create file relation (66% → 100%)
          const relationData = facilityId
            ? createFacilityFileRelationPayload(
                fileCreationResponse,
                facilityId,
                isSuperAdmin
              )
            : createFileRelationPayload(
                fileCreationResponse,
                collectionId,
                fileFacilityId,
                isSuperAdmin
              );
          await fileRelationService.createFileRelations(relationData);

          updateFileProgress(i, 100, UPLOAD_STATUS.SUCCESS);
          successfulUploadsCount++;
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          // 403 = blocked by the Front Door WAF policy (requests whose payload
          // contains <script> are denied), so tell the user what to fix.
          const message =
            error?.response?.status === 403
              ? "Upload blocked: the file contains content that is not allowed (for example <script> tags). Remove it and try again."
              : "Upload failed";
          updateFileProgress(i, 0, UPLOAD_STATUS.ERROR, message);
        }
      }

      setUploadComplete(true);
      if (successfulUploadsCount > 0) onUploadComplete && onUploadComplete();
    } catch (error) {
      console.error("Upload process failed:", error);
      filesToUpload.forEach((_, index) =>
        updateFileProgress(index, 0, UPLOAD_STATUS.ERROR, "Upload failed")
      );
      setUploadComplete(true);
    } finally {
      setIsUploading(false);
    }
  };

  // ── Tab-specific upload triggers ─────────────────────────────────────────
  const handleFileUpload = () => {
    if (selectedFiles.length === 0) return;
    performUpload(selectedFiles);
  };

  const handleTextUpload = () => {
    if (!textContent.trim()) {
      setInputError("Please enter some content.");
      return;
    }
    if (!textFileName.trim()) {
      setInputError("Please enter a file name.");
      return;
    }
    const cleanName = textFileName.trim().replace(/\.md$/i, "");
    const finalName = `${cleanName}.md`;
    const existingNamesLower = existingFileNames.map((n) => n.toLowerCase());
    if (existingNamesLower.includes(finalName.toLowerCase())) {
      setInputError(`A file named "${finalName}" already exists.`);
      return;
    }
    setInputError(null);
    const blob = new Blob([textContent], { type: "text/markdown" });
    const file = new File([blob], finalName, { type: "text/markdown" });
    performUpload([file]);
  };

  const updateFileProgress = (fileIndex, progress, status, error = null) => {
    setUploadProgress((prev) =>
      prev.map((item, index) =>
        index === fileIndex ? { ...item, progress, status, error } : item
      )
    );
  };

  // ── Button state ─────────────────────────────────────────────────────────
  const getButtonState = () => {
    if (isUploading) {
      return {
        text: "Uploading...",
        disabled: true,
        onClick: null,
        icon: <Upload className="w-4 h-4 mr-2 animate-bounce" />,
        className: "bg-blue-500 text-white",
      };
    }
    if (uploadComplete) {
      const successCount = uploadProgress.filter(
        (p) => p.status === UPLOAD_STATUS.SUCCESS
      ).length;
      return successCount > 0
        ? {
            text: "Done",
            disabled: false,
            onClick: handleClose,
            icon: <Check className="w-4 h-4 mr-2" />,
            className: "bg-green-600 text-white",
          }
        : {
            text: "Close",
            disabled: false,
            onClick: handleClose,
            icon: <X className="w-4 h-4 mr-2" />,
            className: "bg-red-600 text-white",
          };
    }
    if (isFacilityMode && activeTab === "text") {
      return {
        text: "Save & Upload",
        disabled: !textContent.trim() || !textFileName.trim(),
        onClick: handleTextUpload,
        icon: <Upload className="w-4 h-4 mr-2" />,
        className: "bg-blue-500 text-white",
      };
    }
    return {
      text: `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? "s" : ""}`,
      disabled: selectedFiles.length === 0 || !!inputError,
      onClick: handleFileUpload,
      icon: <Upload className="w-4 h-4 mr-2" />,
      className: "bg-blue-500 text-white",
    };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case UPLOAD_STATUS.SUCCESS:
        return <Check className="w-4 h-4 text-green-600" />;
      case UPLOAD_STATUS.ERROR:
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case UPLOAD_STATUS.SUCCESS:
        return "bg-green-100 border-green-200";
      case UPLOAD_STATUS.ERROR:
        return "bg-red-100 border-red-200";
      default:
        return "bg-white border-gray-200";
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setInputError(null);
    setUploadProgress([]);
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 ${
        shouldDisableInteractions() ? "cursor-not-allowed" : ""
      }`}
      onClick={handleBackgroundClick}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-xl max-h-[85vh] flex flex-col cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h3 className="text-lg font-semibold">Upload Files</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isUploading}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* File limit progress bar (facility mode) */}
        {maxFiles != null && (
          <div className="mb-3 flex items-center gap-2 flex-shrink-0">
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${
                  currentFileCount >= maxFiles ? "bg-red-500" : "bg-blue-500"
                }`}
                style={{
                  width: `${Math.min((currentFileCount / maxFiles) * 100, 100)}%`,
                }}
              />
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {currentFileCount} / {maxFiles} files
              {remainingSlots != null && remainingSlots > 0 && (
                <span className="text-gray-400"> ({remainingSlots} remaining)</span>
              )}
            </span>
          </div>
        )}

        {/* Tabs (facility mode only, hidden during/after upload) */}
        {isFacilityMode && !isUploading && !uploadComplete && (
          <div className="flex border-b border-gray-200 mb-4 flex-shrink-0">
            <button
              type="button"
              onClick={() => switchTab("file")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "file"
                  ? "border-blue-500 text-blue-500"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Upload File
            </button>
            <button
              type="button"
              onClick={() => switchTab("text")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "text"
                  ? "border-blue-500 text-blue-500"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Type / Paste
            </button>
          </div>
        )}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto min-h-0">

          {/* ── Upload File tab ── */}
          {(!isFacilityMode || activeTab === "file") && !uploadComplete && (
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium">
                Select Files <span className="text-red-500">*</span>
                {isFacilityMode && (
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    .md only
                  </span>
                )}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={isFacilityMode ? ".md" : undefined}
                onChange={handleFileSelect}
                className="block w-full border rounded px-3 py-2 bg-gray-50"
                disabled={
                  isUploading ||
                  (maxFiles != null && currentFileCount >= maxFiles)
                }
              />
              {inputError ? (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {inputError}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {isFacilityMode
                    ? "Only Markdown (.md) files are accepted"
                    : "You can select multiple files at once"}
                  {remainingSlots != null &&
                    remainingSlots > 0 &&
                    ` (up to ${remainingSlots} more)`}
                </p>
              )}
            </div>
          )}

          {/* ── Type / Paste tab ── */}
          {isFacilityMode &&
            activeTab === "text" &&
            !isUploading &&
            !uploadComplete && (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={textContent}
                    onChange={(e) => {
                      setTextContent(e.target.value);
                      if (inputError) setInputError(null);
                    }}
                    placeholder="Type or paste your Markdown content here..."
                    rows={10}
                    className="w-full border rounded px-3 py-2 text-sm font-mono bg-gray-50 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    File name <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={textFileName}
                      onChange={(e) => {
                        setTextFileName(e.target.value);
                        if (inputError) setInputError(null);
                      }}
                      placeholder="e.g. clinical-guidelines"
                      className="flex-1 border rounded-l px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="border border-l-0 rounded-r px-3 py-2 text-sm bg-gray-100 text-gray-500 select-none">
                      .md
                    </span>
                  </div>
                </div>
                {inputError && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {inputError}
                  </p>
                )}
              </div>
            )}

          {/* ── Upload progress list ── */}
          {uploadProgress.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2">
                {uploadComplete ? "Upload Results:" : "Files to Upload:"}
              </h4>
              <div className="space-y-2">
                {uploadProgress.map((fileProgress, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-3 ${getStatusColor(
                      fileProgress.status
                    )}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium truncate flex-1 mr-2">
                        {fileProgress.name}
                      </span>
                      {getStatusIcon(fileProgress.status)}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          fileProgress.status === UPLOAD_STATUS.SUCCESS
                            ? "bg-green-600"
                            : fileProgress.status === UPLOAD_STATUS.ERROR
                            ? "bg-red-600"
                            : "bg-blue-500"
                        }`}
                        style={{ width: `${fileProgress.progress}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-600">
                      {fileProgress.status === UPLOAD_STATUS.SUCCESS &&
                        "Upload completed successfully"}
                      {fileProgress.status === UPLOAD_STATUS.ERROR &&
                        (fileProgress.error || "Upload failed")}
                      {fileProgress.status === UPLOAD_STATUS.UPLOADING &&
                        `${fileProgress.progress}% complete`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 flex-shrink-0">
          {!uploadComplete && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
          )}
          {(() => {
            const buttonState = getButtonState();
            return (
              <Button
                onClick={buttonState.onClick}
                disabled={buttonState.disabled}
                className={buttonState.className}
              >
                {buttonState.icon}
                {buttonState.text}
              </Button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};
